/**
 * Real-account auth helpers.
 *
 * The PWA never touches the raw WHB JWT — it lives in an HttpOnly cookie on
 * whb-client.pages.dev set by functions/api/session.ts. The helpers here
 * coordinate the bridge hand-off with whitehatbots.com and wrap fetch() so
 * callers get same-origin requests to /api/* without thinking about cookies.
 *
 * See docs/REAL-LOGIN-RECON.md and docs/WHB_BRIDGE_SPEC.md.
 */

import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth-store";

const BRIDGE_ORIGIN = "https://app.whitehatbots.com";
const BRIDGE_PATH = "/bridge";
const BRIDGE_VERSION = "1";

function pwaOrigin(): string {
  return typeof window !== "undefined" ? window.location.origin : "";
}

export function buildBridgeUrl(
  kind: "hand-off" | "logout",
  returnPath: string,
): string {
  const returnUrl = `${pwaOrigin()}${returnPath}`;
  const params = new URLSearchParams({
    v: BRIDGE_VERSION,
    action: kind,
    origin: "whb-client",
    return: returnUrl,
  });
  return `${BRIDGE_ORIGIN}${BRIDGE_PATH}?${params.toString()}`;
}

/** Start a real-account login. Top-level redirect — works in iOS PWA too. */
export function startRealLogin(opts: { reason?: "fresh" | "expired" } = {}): void {
  const ret = opts.reason === "expired" ? "/auth/callback?reason=expired" : "/auth/callback";
  window.location.assign(buildBridgeUrl("hand-off", ret));
}

/**
 * Full logout: clear our cookie AND ask the WHB bridge to clear its own
 * localStorage. On desktop we use a popup + postMessage so the user stays
 * on the PWA. On iOS standalone PWA (where popups are flaky) we fall back
 * to a top-level redirect and come back via `/auth/callback#logged-out=1`.
 */
export async function logoutFullFlow(): Promise<void> {
  // 1) Kill our HttpOnly cookie first, so even if the bridge step fails the
  //    PWA is definitively signed out on its end.
  try {
    await fetch("/api/logout", { method: "POST", credentials: "include" });
  } catch {
    // swallow — cookie is gone either way once the server responds,
    // and the local store clear below still happens.
  }

  // 2) Clear local auth state.
  useAuthStore.getState().signOut();

  // 3) Tear down the WHB-side session.
  const isStandalonePwa =
    typeof window !== "undefined" &&
    window.matchMedia?.("(display-mode: standalone)")?.matches === true;

  if (isStandalonePwa) {
    window.location.assign(buildBridgeUrl("logout", "/auth/callback"));
    return;
  }

  // Desktop / browser tab: popup + postMessage. The bridge calls
  // `opener.postMessage({ type: 'whb-logout-done' }, opener.origin)` and
  // closes itself. If the popup is blocked, fall back to a redirect.
  const popup = window.open(
    buildBridgeUrl("logout", "/auth/callback"),
    "whb-logout",
    "width=400,height=500,noopener=no",
  );

  if (!popup || popup.closed) {
    window.location.assign(buildBridgeUrl("logout", "/auth/callback"));
    return;
  }

  // Best-effort close listener. Not critical — even if this never fires,
  // the popup closes itself and the WHB session is gone. We just watch for
  // confirmation so we can toast.
  const handler = (ev: MessageEvent) => {
    if (ev.origin !== BRIDGE_ORIGIN) return;
    if ((ev.data as { type?: string } | null)?.type === "whb-logout-done") {
      window.removeEventListener("message", handler);
    }
  };
  window.addEventListener("message", handler);
}

/**
 * Hook returning a single "sign me out, whichever kind of session this is"
 * callback. Call this from any button that used to wire `signOut()` +
 * `navigate("/sign-in")` directly.
 *
 *  - Demo session  → clear local store, navigate to /sign-in.
 *  - Real session  → run the full logout flow (clear cookie, tear down
 *                    WHB localStorage via bridge popup/redirect). The
 *                    real flow handles its own navigation; we don't push
 *                    /sign-in here because the popup UX keeps the user on
 *                    the current screen until the bridge postMessages
 *                    back, and the redirect UX goes through /auth/callback.
 */
export function useSignOutAny(): () => void {
  const navigate = useNavigate();
  return () => {
    const { kind, signOut } = useAuthStore.getState();
    if (kind === "real") {
      void logoutFullFlow();
      return;
    }
    signOut();
    navigate("/sign-in");
  };
}

/**
 * Same-origin fetch wrapper. Use this for every real-API call so the
 * HttpOnly cookie rides along and we get centralized 401 handling.
 *
 * Usage: whbFetch('/api/bots') returns the decoded JSON or throws.
 */
export async function whbFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, { ...init, credentials: "include" });

  if (res.status === 401) {
    // Our cookie was rejected by upstream. Clear local state and bounce
    // through the bridge to attempt a silent re-auth (works if WHB session
    // is still alive; otherwise user sees the sign-in picker).
    useAuthStore.getState().signOut();
    startRealLogin({ reason: "expired" });
    // Reject so callers don't try to unwrap a body.
    throw new Error("session expired");
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`request failed ${res.status}: ${text.slice(0, 200)}`);
  }

  // Allow 204 / empty bodies.
  if (res.status === 204 || res.headers.get("Content-Length") === "0") {
    return undefined as T;
  }
  return (await res.json()) as T;
}
